import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import toast from 'react-hot-toast';

import { useConvertReport, usePeriodById } from '../../../services/period.service';
import { usePeriodLiveRefresh } from '../../../hooks/usePeriodLiveRefresh';
import { useSignalR } from '../../../providers/SignalR';
import {
  useAutomateExtraction,
  useAutomateReExtraction,
  useAutomateReportGeneration,
  useAutomateTransformation,
  useCopyAIFs,
  useCopyJurisdictions
} from '../../../services/automation.service';
import {
  useGetDetailedExtractionStatsByPeriodId,
  useGetDetailedTransformationStatsByPeriodId,
  useGetFundsByPeriodId
} from '../../../services/funds.service';
import { usePeriodFiles } from '../../../services/period.service';
import { useGetJurisdictions } from '../../../services/jurisdiction.service';
import { useTemplateFunds } from '../../../pages/SourceFilesV2/hooks/useTemplateFunds';
import { compareTemplateFunds } from '../../../pages/SourceFilesV2/lib/aifTemplate';
import { isFileInProgress } from '../../../pages/SourceFilesV2/lib/fileKinds';
import { extractApiError } from '../../../utils';
import {
  GET_EXTRACTION_STATS,
  GET_FUNDS_BY_PERIOD,
  GET_TRANSFORMATION_STATS
} from '../../../consts/queryKeys';
import { withLiveStats } from '../../../utils/stageStats';
import { REPORT_TYPES } from '../../../consts/reportTypes';
import {
  useCopySheetCategories,
  useGetSheetCategoriesForPeriod
} from '../../../services/sheetCategories.service';
import {
  askBefore,
  runTiles,
  tilesCaption,
  placeFor,
  runNarration,
  inFlightCount,
  isHandPlace,
  runStatus,
  samePlace,
  workbookLetsItGo,
  STAGE_FOR_STEP,
  sheetsToReExtract,
  STEP_LABELS,
  troubleSheets,
  runLostItsPeriod,
  nothingToTransform,
  startedNoTransformation,
  includedFundCount
} from './steps';

const STORE_KEY = 'dx.period-run';

try {
  window.sessionStorage.removeItem('dx.mapping-carried');
} catch {
  /* Storage refused; there was nothing important in it. */
}

const RESUME_WINDOW_MS = 60 * 60 * 1000;

const OUTCOME_KEY = 'dx.period-run-outcome';

/* How long the finished band stays before it takes itself away. */
const OUTCOME_SHOWN_MS = 20_000;

const DRIVER_KEY = periodId => `dx.period-run-driver:${periodId}`;
const DRIVER_BEAT_MS = 4_000;
const DRIVER_STALE_MS = 12_000;

/* How often the per-sheet lists are re-asked for while a sheet stage is running. */
const SHEET_STATS_BEAT_MS = 10_000;

const readOutcome = () => {
  try {
    const raw = window.sessionStorage.getItem(OUTCOME_KEY);
    const stored = raw ? JSON.parse(raw) : null;
    if (!stored?.kind) return null;
    if (!stored.at || Date.now() - stored.at > RESUME_WINDOW_MS) {
      window.sessionStorage.removeItem(OUTCOME_KEY);
      return null;
    }
    return stored;
  } catch {
    return null;
  }
};

const writeOutcome = outcome => {
  try {
    if (outcome) window.sessionStorage.setItem(OUTCOME_KEY, JSON.stringify(outcome));
    else window.sessionStorage.removeItem(OUTCOME_KEY);
  } catch {
    /* Storage refused; the band lives as long as the page does, as it used to. */
  }
};

const readStored = () => {
  try {
    const raw = window.sessionStorage.getItem(STORE_KEY);
    const stored = raw ? JSON.parse(raw) : null;
    if (!stored?.periodId || !stored?.workflowId) return null;
    /* No stamp at all means it was written before runs carried one — old enough by definition. */
    if (!stored.startedAt || Date.now() - stored.startedAt > RESUME_WINDOW_MS) {
      window.sessionStorage.removeItem(STORE_KEY);
      return null;
    }
    return stored;
  } catch {
    return null;
  }
};

const writeStored = run => {
  try {
    if (run) window.sessionStorage.setItem(STORE_KEY, JSON.stringify(run));
    else window.sessionStorage.removeItem(STORE_KEY);
  } catch {
    /* A private-mode window with storage disabled loses the run on reload and nothing else. */
  }
};

/* How long a step may sit between "asked for" and "visibly happening" before the band stops… */
const STALL_MS = 90_000;

const MAX_ATTEMPTS = 2;

/* How long a finished step is left on screen before the next one is asked for. */
const SETTLE_MS = 3200;

const SETTLES = new Set(['extraction', 'transformation', 'report']);

/* How long after a copy settles before its emptiness is called emptiness. */
const ASKED_GRACE_MS = 8000;

const SURVIVES_FAILURE = new Set(['mapping']);

/* How long after a stage's counts last changed it is still taken to be working. */
const MOVING_MS = 30_000;

/* And how long a stage may claim to be working without a single count changing before the band… */
const STUCK_MS = 5 * 60 * 1000;

// Stable identity, so the default never re-memoises the state below.
const EMPTY_ABANDONED = [];

/* The shape of a stage's counts, as one comparable string. */
const countsMark = (period, step) => {
  const key = step === 'transformation' ? 'transformation_stats' : 'extraction_stats';
  return JSON.stringify(period?.[key] ?? {});
};

/* The server knows a step is already running before the period's own counts do. */
const ALREADY_RUNNING =
  /already (in progress|running|started)|is in progress|in progress for this/i;

/* The period record is keyed by [prefix, customKey, periodId]. */
const invalidatePeriod = (queryClient, periodId) =>
  queryClient.invalidateQueries({
    predicate: query =>
      Array.isArray(query.queryKey) &&
      query.queryKey[0] === 'GET_PERIOD_BY_ID' &&
      query.queryKey[2] === periodId
  });

const EMPTY = [];

/* Copied funds are read from their own queries, which no period-record refresh reaches. */
const invalidateFunds = (queryClient, periodId) => {
  queryClient.invalidateQueries([GET_FUNDS_BY_PERIOD, periodId]);
  queryClient.invalidateQueries(['GET_AUTOMATION_FUND_MAPPINGS', periodId]);
};

/* The periods page draws its rows from the workflow, and reads it under `GET_WORKFLOW_BY_ID`. */
const invalidateWorkflow = (queryClient, workflowId) => {
  queryClient.invalidateQueries(['GET_WORKFLOW_BY_ID', workflowId]);
  queryClient.invalidateQueries(['GET_WORKFLOW']);
};

const PeriodRunContext = createContext(null);

/* What the chrome sees when there is no provider above it. */
const NO_RUN = {
  run: null,
  period: null,
  state: null,
  narration: null,
  question: null,
  outcome: null,
  isStalled: false,
  /* Everything the provider hands out has to be here too, with the shape a reader can use… */
  tiles: EMPTY,
  tilesCaption: null,
  place: null,
  waitingNote: null,
  stuckMinutes: null,
  isFollowing: false,
  isDriver: true,
  isStarting: false,
  isRunningPeriod: () => false,
  ownsStage: () => false,
  go: () => {},
  moveOn: () => {},
  hold: () => {},
  retrySheets: EMPTY,
  troubled: EMPTY,
  canRetrySheets: false,
  isRetrying: false,
  start: () => {},
  stop: () => {},
  confirm: () => {},
  reExtract: () => {},
  refire: () => {},
  retry: () => {},
  dismiss: () => {},
  resumeFollowing: () => {}
};

export const usePeriodRun = () => useContext(PeriodRunContext) ?? NO_RUN;

/* One press, and the machine goes as far as it can — through the pages, not behind them. */
export const PeriodRunProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const [run, setRun] = useState(readStored);
  const [question, setQuestion] = useState(null);
  /* For `fire`, which must not be re-created on every question. */
  const questionRef = useRef(null);
  const [outcome, setOutcomeState] = useState(readOutcome);
  /* Written wherever it is set, so the two cannot drift. */
  const setOutcome = useCallback(next => {
    const stamped = next ? { ...next, at: Date.now() } : null;
    setOutcomeState(stamped);
    writeOutcome(stamped);
  }, []);

  /* Which tab is driving this period, and whether it is this one. */
  const driverIdRef = useRef(
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
  );
  const [isDriver, setIsDriver] = useState(true);
  const isDriverRef = useRef(true);
  useEffect(() => {
    isDriverRef.current = isDriver;
  }, [isDriver]);

  useEffect(() => {
    const periodId = run?.periodId;
    if (!periodId) {
      setIsDriver(true);
      return undefined;
    }
    const key = DRIVER_KEY(periodId);
    const me = driverIdRef.current;

    const claim = () => {
      let held = null;
      try {
        held = JSON.parse(window.localStorage.getItem(key) || 'null');
      } catch {
        held = null;
      }
      const alive = !!held?.at && Date.now() - held.at < DRIVER_STALE_MS;
      const ours = !alive || held.driver === me;
      if (ours) {
        try {
          window.localStorage.setItem(key, JSON.stringify({ driver: me, at: Date.now() }));
        } catch {
          /* Storage refused. */
        }
      }
      setIsDriver(ours);
    };

    claim();
    const beat = setInterval(claim, DRIVER_BEAT_MS);
    return () => {
      clearInterval(beat);
      /* Hand it over on the way out rather than leaving the next tab to wait out the staleness. */
      try {
        const held = JSON.parse(window.localStorage.getItem(key) || 'null');
        if (held?.driver === me) window.localStorage.removeItem(key);
      } catch {
        /* Nothing to release. */
      }
    };
  }, [run?.periodId]);

  /* Closing the tab ends the run, and nothing else says so. */
  useEffect(() => {
    if (!run) return undefined;
    const warn = event => {
      event.preventDefault();
      // Chrome ignores the string and shows its own words; Firefox needs the property set at all.
      event.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [run]);

  useEffect(() => {
    if (!outcome || outcome.kind === 'error') return undefined;
    const left = OUTCOME_SHOWN_MS - (Date.now() - (outcome.at || 0));
    if (left <= 0) {
      setOutcome(null);
      return undefined;
    }
    const timer = setTimeout(() => setOutcome(null), left);
    return () => clearTimeout(timer);
  }, [outcome, setOutcome]);
  /* What we have just asked the server for, and have not yet seen in the period. */
  const [fired, setFired] = useState(null);
  const [isStalled, setIsStalled] = useState(false);
  /* What the server said when it declined to start a step, kept so the band can pass it on. */
  const [waitingNote, setWaitingNote] = useState(null);
  const lastNavRef = useRef(null);
  /* One ask at a time, held where a re-render cannot miss it. */
  const inFlightRef = useRef(null);
  /* The run as the shared error handler sees it — it is built once and must not go stale. */
  const runRef = useRef(null);
  const retriedRef = useRef(false);
  /* How many times each step has been asked for in this run. */
  const attemptsRef = useRef({});

  useEffect(() => {
    runRef.current = run;
    writeStored(run);
  }, [run]);

  const {
    data: period,
    isError: periodUnreadable,
    error: periodError
  } = usePeriodById({
    periodId: run?.periodId,
    queryParams: { enabled: !!run?.periodId }
  });
  // The run is not always on a workflow page, so it listens for its period itself.
  usePeriodLiveRefresh(run?.periodId);

  const copyAIFs = useCopyAIFs();
  const copyJurisdictions = useCopyJurisdictions();
  const copyMapping = useCopySheetCategories({ periodId: run?.periodId });
  const automateExtraction = useAutomateExtraction();
  const automateTransformation = useAutomateTransformation();
  const generateReport = useAutomateReportGeneration();
  const convertReport = useConvertReport();
  const reExtract = useAutomateReExtraction();

  const {
    data: sheetCategories,
    isLoading: isLoadingCategories,
    isFetching: isReadingCategories
  } = useGetSheetCategoriesForPeriod({
    periodId: run?.periodId,
    queryParams: { enabled: !!run?.periodId }
  });

  const { data: extractionRows, isLoading: isLoadingRows } =
    useGetDetailedExtractionStatsByPeriodId({
      periodId: run?.periodId,
      queryParams: { enabled: !!run?.periodId }
    });

  const { data: transformationRows } = useGetDetailedTransformationStatsByPeriodId({
    periodId: run?.periodId,
    queryParams: { enabled: !!run?.periodId }
  });

  /* Whether the report would have any AIF in it at all. */
  const { data: runJurisdictions } = useGetJurisdictions({
    periodId: run?.periodId,
    queryParams: { enabled: !!run?.periodId }
  });
  const noFundsIncluded = includedFundCount(runJurisdictions) === 0;

  /* Memoised so the context value below does not change identity on every render. */
  const retrySheets = useMemo(
    () => (question?.retry ? sheetsToReExtract(extractionRows) : EMPTY),
    [question?.retry, extractionRows]
  );

  const isConversion = run?.reportType === REPORT_TYPES.CONVERSION;

  /* What the parse answered per fund — the only account of what the report actually covers. */
  const parseSummaryRef = useRef(null);

  /* A fund with no AIF Template column drops out of the report silently, so read it beforehand. */
  const checksWorkbook = isConversion && !!run?.periodId;
  const { data: conversionFiles } = usePeriodFiles({
    periodId: run?.periodId,
    queryOptions: { enabled: checksWorkbook }
  });
  const workbooks = useMemo(
    () => (conversionFiles || []).filter(file => file.period_id === run?.periodId),
    [conversionFiles, run?.periodId]
  );
  const workbook = useTemplateFunds(checksWorkbook ? workbooks : EMPTY);
  const { data: conversionFunds } = useGetFundsByPeriodId({
    periodId: run?.periodId,
    queryParams: { enabled: checksWorkbook }
  });
  /* Shaped like the short sheets, so the question lists them the way it lists those. */
  const missingAifs = useMemo(() => {
    if (!checksWorkbook || !workbook.isRead) return EMPTY;
    const names = (conversionFunds || []).map(fund => fund.name).filter(Boolean);
    if (!names.length) return EMPTY;
    const { aifs } = compareTemplateFunds(names, workbook.columns);
    return aifs
      .filter(row => row.state !== 'found')
      .map(row => ({
        key: row.name,
        sheet: row.name,
        file: workbooks[0]?.name,
        why:
          row.state === 'renamed'
            ? `in AIF Template as “${row.column}”`
            : 'no column in AIF Template'
      }));
  }, [checksWorkbook, workbook.isRead, workbook.columns, conversionFunds, workbooks]);

  /* What the question lists: the short sheets, or a conversion's funds without a column. */
  const troubled = useMemo(() => {
    if (!question) return EMPTY;
    return missingAifs.length ? missingAifs : troubleSheets(extractionRows);
  }, [question, missingAifs, extractionRows]);

  /* A conversion's report is not made by automation and never has been. */
  const mutationFor = step =>
    ({
      funds: copyAIFs,
      jurisdictions: copyJurisdictions,
      mapping: copyMapping,
      extraction: automateExtraction,
      transformation: automateTransformation,
      report: isConversion ? convertReport : generateReport
    })[step];

  /* The automation endpoints all take the period in the body; the conversion one takes it in… */
  const argsFor = step =>
    step === 'report' && isConversion
      ? { periodId: run.periodId, workflowId: run.workflowId }
      : { period_id: run.periodId };

  /* One place where a step's failure is judged, because there are two ways to ask for one. */
  const failed = useCallback(
    step => err => {
      const text = extractApiError(err, `Could not start ${step}.`);

      /* The step is happening, asked for by us or by something else. */
      if (ALREADY_RUNNING.test(text)) {
        setWaitingNote({ step, text });
        return;
      }

      if (SURVIVES_FAILURE.has(step)) {
        setFired(null);
        setTriedAt(current => ({ ...current, [step]: Date.now() }));
        toast.error(text, { position: 'bottom-center' });
        return;
      }

      setFired(null);
      setRun(null);
      /* The band says it, in the place the run has been speaking from all along. */
      setOutcome({ kind: 'error', step, text, ...(runRef.current || {}) });
    },
    []
  );

  const fire = useCallback(
    step => {
      const mutation = mutationFor(step);
      if (!mutation || !run || inFlightRef.current) return;
      /* Watching, not driving: another tab holds this period and is asking for its steps. */
      if (!isDriverRef.current) return;
      /* And never while a question is standing. */
      if (questionRef.current) return;

      attemptsRef.current[step] = (attemptsRef.current[step] || 0) + 1;
      inFlightRef.current = step;
      setFired({ step, at: Date.now() });
      setIsStalled(false);
      setWaitingNote(null);

      mutation.mutate(argsFor(step), {
        onSettled: () => {
          inFlightRef.current = null;
          if (step === 'mapping' || step === 'jurisdictions')
            setTriedAt(current => ({ ...current, [step]: Date.now() }));
        },
        onSuccess: data => {
          if (step === 'report' && isConversion && Array.isArray(data)) {
            parseSummaryRef.current = data;
          }
          // The server had no transformation to start: the run goes on to the report.
          if (step === 'transformation' && startedNoTransformation(data))
            setRun(current => (current ? { ...current, nothingToTransform: true } : current));
          /* The page's list and the workbook check both read the funds this step just copied. */
          if (step === 'funds') invalidateFunds(queryClient, run.periodId);
          if (step === 'jurisdictions')
            queryClient.invalidateQueries(['GET_JURISDICTIONS', run.periodId]);
          invalidatePeriod(queryClient, run.periodId);
          invalidateWorkflow(queryClient, run.workflowId);
        },
        onError: failed(step)
      });
    },
    [run, isConversion, queryClient]
  );

  /* The one step the period's own fields cannot announce. */
  const mapped = useMemo(() => {
    const rows = Array.isArray(sheetCategories)
      ? sheetCategories
      : (sheetCategories?.results ?? []);
    return rows.length > 0;
  }, [sheetCategories]);

  /* Both halves of the gate live on the run itself, which is what survives a reload. */

  /* When each copy that can legitimately come back with nothing was last asked for. */
  const [triedAt, setTriedAt] = useState({});

  /* The step that has just finished and is being held on screen, if any. */
  const [settled, setSettled] = useState(null);
  const workingOnRef = useRef(null);

  const readPeriod = useMemo(
    () =>
      withLiveStats(period, {
        extractionRecords: extractionRows,
        transformationRecords: transformationRows
      }),
    [period, extractionRows, transformationRows]
  );

  /* ── When each stage was last seen working ── Stamped only while a sheet is actually in… */
  const [ranAt, setRanAt] = useState({});
  useEffect(() => {
    if (!readPeriod) return;
    const now = Date.now();
    const seen = {};
    for (const step of ['extraction', 'transformation'])
      if (inFlightCount(readPeriod, step) > 0) seen[step] = now;
    if (Object.keys(seen).length) setRanAt(prev => ({ ...prev, ...seen }));
  }, [readPeriod]);

  /* And a nudge when the window runs out, because nothing else would notice. */
  const [, expire] = useReducer(count => count + 1, 0);
  useEffect(() => {
    const latest = Math.max(0, ...Object.values(ranAt));
    if (!latest) return undefined;
    const left = MOVING_MS - (Date.now() - latest);
    if (left <= 0) return undefined;
    const timer = setTimeout(expire, left + 100);
    return () => clearTimeout(timer);
  }, [ranAt]);

  const moving = useCallback(step => Date.now() - (ranAt[step] || 0) < MOVING_MS, [ranAt]);

  /* A page saying "not yet". */
  const [heldBy, setHeldBy] = useState(null);
  const hold = useCallback((reason, on) => {
    setHeldBy(current => (on ? reason : current === reason ? null : current));
  }, []);

  /* The grace above ends on the clock, and nothing else would notice. */
  const [mappingTick, tickMapping] = useReducer(count => count + 1, 0);
  useEffect(() => {
    const latest = Math.max(0, ...Object.values(triedAt));
    if (!latest) return undefined;
    const left = ASKED_GRACE_MS - (Date.now() - latest);
    if (left <= 0) return undefined;
    const timer = setTimeout(tickMapping, left + 200);
    return () => clearTimeout(timer);
  }, [triedAt]);

  const [abandoned, setAbandoned] = useState(EMPTY_ABANDONED);
  const skipTransformation = useMemo(
    () => !!run?.nothingToTransform || nothingToTransform(transformationRows),
    [run?.nothingToTransform, transformationRows]
  );

  const rawState = useMemo(
    () =>
      run && readPeriod
        ? runStatus(readPeriod, run.reportType, {
            mapped,
            moving,
            abandoned,
            skipTransformation,
            mappingTried:
              !!triedAt.mapping &&
              !isReadingCategories &&
              Date.now() - triedAt.mapping > ASKED_GRACE_MS,
            jurisdictionsTried:
              !!triedAt.jurisdictions && Date.now() - triedAt.jurisdictions > ASKED_GRACE_MS
          })
        : null,
    [
      run,
      readPeriod,
      mapped,
      moving,
      abandoned,
      skipTransformation,
      triedAt,
      isReadingCategories,
      mappingTick
    ]
  );
  /* A hold beats the counts, and carries its own reason rather than collapsing to "files". */
  const state = heldBy ? { status: 'blocked', reason: heldBy } : rawState;
  questionRef.current = question;
  const stateRef = useRef(state);
  stateRef.current = state;

  const awaitingGo =
    !!run?.needsGo &&
    !run?.released &&
    !!state &&
    /* Any hold at all, not just the "no files" one. */
    !heldBy &&
    !(state.status === 'blocked' && state.reason === 'files');

  const [movedAt, setMovedAt] = useState({});
  const marksRef = useRef({});
  useEffect(() => {
    if (!readPeriod) return;
    const now = Date.now();
    const moved = {};
    for (const step of ['extraction', 'transformation']) {
      const mark = countsMark(readPeriod, step);
      if (marksRef.current[step] === undefined) {
        marksRef.current[step] = mark;
        moved[step] = now;
        continue;
      }
      if (marksRef.current[step] === mark) continue;
      marksRef.current[step] = mark;
      moved[step] = now;
    }
    if (Object.keys(moved).length) setMovedAt(prev => ({ ...prev, ...moved }));
  }, [readPeriod]);

  const [stuckTick, tickStuck] = useReducer(count => count + 1, 0);
  const stuckStep = (shownStateStep => {
    if (!shownStateStep) return null;
    const since = movedAt[shownStateStep];
    if (!since) return null;
    return Date.now() - since >= STUCK_MS ? shownStateStep : null;
  })(rawState?.status === 'busy' ? rawState.step : null);
  useEffect(() => {
    if (rawState?.status !== 'busy' || !rawState.step) return undefined;
    const since = movedAt[rawState.step];
    if (!since) return undefined;
    const left = STUCK_MS - (Date.now() - since);
    if (left <= 0) return undefined;
    const timer = setTimeout(tickStuck, left + 500);
    return () => clearTimeout(timer);
  }, [rawState?.status, rawState?.step, movedAt, stuckTick]);

  const stuckMinutes = stuckStep ? Math.floor((Date.now() - movedAt[stuckStep]) / 60_000) : null;

  useEffect(() => {
    if (!run || question || outcome || !readPeriod || !state) return;

    /* Waiting to be let go. Nothing is asked for and nothing is decided. */
    if (awaitingGo) return;

    if (settled) return;

    /* "No rows read yet" is indistinguishable from "no rows". */
    if (readPeriod.file_count && isLoadingCategories) return;

    /* ── The beat between steps ── Decided here, in the same pass that would otherwise ask for… */
    const busyNow =
      state.status === 'busy' || state.status === 'asked' ? state.step : fired?.step || null;
    if (busyNow) workingOnRef.current = busyNow;
    else {
      const justFinished = workingOnRef.current;
      /* Still on the same step: the period has said nothing new yet. */
      if (justFinished && state.step !== justFinished) {
        workingOnRef.current = null;
        /* Only a step that got somewhere, and only one worth waiting on. */
        if (SETTLES.has(justFinished) && state.status !== 'blocked') {
          setSettled({ step: justFinished });
          return;
        }
      }
    }

    /* Asked to stop, and the step it was on has ended. This is where "after this step" happens. */
    if (run.stopAfter && state.status !== 'busy' && state.status !== 'asked') {
      setRun(null);
      setFired(null);
      setWaitingNote(null);
      inFlightRef.current = null;
      return;
    }

    if (state.status !== 'step') {
      if (fired) setFired(null);
      if (isStalled) setIsStalled(false);
      if (state.status === 'done') {
        /* A run that has been followed all the way ends where it was going. */
        const finish = placeFor({ status: 'step', step: 'report' }, run);
        if (run.follow && finish && !isHandPlace(`${location.pathname}${location.search}`)) {
          lastNavRef.current = finish;
          navigate(finish);
        }
        setOutcome({ kind: 'done', ...run, parse: parseSummaryRef.current || undefined });
        setRun(null);
      }
      /* `busy` waits for the machine, `blocked` waits for a person. */
      return;
    }

    /* Asked for, not yet visible in the data: wait for the next read rather than asking twice. */
    if (fired?.step === state.step) return;

    /* A step asked for BEHIND where the period currently stands. */
    if (fired?.strict) {
      if (fired.mark === undefined || fired.mark === countsMark(readPeriod, fired.step)) return;
      setFired({ ...fired, strict: false });
      return;
    }

    if (run.stopAfter) return;

    const ask = askBefore(readPeriod, state.step, {
      canRetry: !retriedRef.current,
      missingAifs,
      skipTransformation
    });
    if (ask) {
      setQuestion(ask);
      return;
    }

    const attempts = attemptsRef.current[state.step] || 0;
    if (attempts >= MAX_ATTEMPTS) {
      setRun(null);
      setOutcome({
        kind: 'error',
        step: state.step,
        text: `${STEP_LABELS[state.step] || state.step} ran without producing anything, twice. Something in the step itself needs looking at.`,
        ...run
      });
      return;
    }
    /* Counted by `fire` itself, and only when it really asks. */
    fire(state.step);
  }, [
    awaitingGo,
    readPeriod,
    run,
    state,
    isLoadingCategories,
    question,
    outcome,
    fired,
    fire,
    settled,
    isStalled
  ]);

  useEffect(() => {
    if (!settled) return undefined;
    const timer = setTimeout(() => setSettled(null), SETTLE_MS);
    return () => clearTimeout(timer);
  }, [settled]);

  /* A run that ends, or is stopped, takes its held step with it. */
  useEffect(() => {
    if (run) return;
    workingOnRef.current = null;
    if (settled) setSettled(null);
  }, [run, settled]);

  /* Owed a press, but a conversion has already answered everything the press would decide. */
  useEffect(() => {
    if (!run || !isConversion || !awaitingGo) return;
    const clean = workbookLetsItGo({
      isRead: workbook.isRead,
      error: workbook.error,
      withoutTemplate: workbook.withoutTemplate,
      missing: missingAifs,
      reading: workbooks.some(isFileInProgress)
    });
    if (!clean) return;
    setRun(current => (current ? { ...current, released: true } : current));
  }, [run, isConversion, awaitingGo, workbook, missingAifs, workbooks]);

  /* This run has waited for files at some point, so a press is owed before it goes on. */
  useEffect(() => {
    if (!run || run.needsGo || run.released) return;
    if (state?.status !== 'blocked' || state.reason !== 'files') return;
    setRun(current => (current ? { ...current, needsGo: true } : current));
  }, [run, state]);

  /* A run whose period can't be read is not a run; a deleted period's cached copy doesn't count. */
  useEffect(() => {
    if (!run) return;
    if (!runLostItsPeriod({ isError: periodUnreadable, data: period, error: periodError })) return;
    setRun(null);
    setFired(null);
    setQuestion(null);
  }, [run, periodUnreadable, period, periodError]);

  /* The same for a finished run's band: an error outcome stays until closed, even after its period is deleted. */
  const outcomePeriodId =
    outcome?.periodId && outcome.periodId !== run?.periodId ? outcome.periodId : null;
  const outcomePeriod = usePeriodById({
    periodId: outcomePeriodId,
    queryParams: { enabled: !!outcomePeriodId, refetchInterval: 15000 }
  });
  useEffect(() => {
    if (!outcomePeriodId) return;
    const { isError, data, error } = outcomePeriod;
    if (runLostItsPeriod({ isError, data, error })) setOutcome(null);
  }, [outcomePeriodId, outcomePeriod.isError, outcomePeriod.data, outcomePeriod.error, setOutcome]);

  /* The clock starts when the run DECIDES on a step, not only when it manages to ask for one. */
  const pendingStep = state?.status === 'step' ? state.step : null;
  useEffect(() => {
    if (!fired && !pendingStep) return undefined;
    const timer = setTimeout(() => setIsStalled(true), STALL_MS);
    return () => clearTimeout(timer);
  }, [fired, pendingStep]);

  useEffect(() => {
    if (!question || !run || !period) return;
    if (
      askBefore(readPeriod, question.step, {
        canRetry: !retriedRef.current,
        missingAifs,
        skipTransformation
      })
    )
      return;
    setQuestion(null);
  }, [question, run, readPeriod, missingAifs, skipTransformation]);

  /* What the run says it is doing, which is not always what the period says comes next. */
  /* The finish, decided here rather than one render later. */
  const pendingSettle =
    !settled &&
    !question &&
    !outcome &&
    state?.status === 'step' &&
    workingOnRef.current &&
    workingOnRef.current !== state.step &&
    SETTLES.has(workingOnRef.current)
      ? workingOnRef.current
      : null;

  const shownState = awaitingGo
    ? { status: 'ready' }
    : question
      ? /* A question is up. Whatever the counts have begun to say about the next step, nothing is
         happening until somebody answers — and the band was announcing "Starting transformation"
         over a modal asking whether the transformation should happen at all. */
        { status: 'asking', step: question.step }
      : settled
        ? { status: 'settled', step: settled.step }
        : pendingSettle
          ? { status: 'settled', step: pendingSettle }
          : fired
            ? { status: 'asked', step: fired.step }
            : state;

  const tiles = useMemo(() => {
    const step = shownState?.step;
    if (step === 'extraction') return runTiles(step, extractionRows);
    if (step === 'transformation') return runTiles(step, transformationRows);
    return EMPTY;
  }, [shownState?.step, extractionRows, transformationRows]);

  /* And the per-sheet lists are read by asking, while there is no connection to tell us. */
  const { isConnected } = useSignalR();
  useEffect(() => {
    const step = shownState?.step;
    if (isConnected || !run || (step !== 'extraction' && step !== 'transformation'))
      return undefined;
    const key = step === 'extraction' ? GET_EXTRACTION_STATS : GET_TRANSFORMATION_STATS;
    const beat = setInterval(() => {
      queryClient.invalidateQueries([key, run.periodId]);
    }, SHEET_STATS_BEAT_MS);
    return () => clearInterval(beat);
  }, [run, shownState?.step, queryClient, isConnected]);

  const place = shownState && run ? placeFor(shownState, run) : null;

  useEffect(() => {
    if (!run || run.follow) return;
    if (!shownState?.step || !run.leftAt || shownState.step === run.leftAt) return;
    setRun(current => (current ? { ...current, follow: true, leftAt: undefined } : current));
  }, [run, shownState?.step]);

  /* Following the run from page to page — except off the pages where the work is a person's. */
  useEffect(() => {
    if (!run?.follow || !place) return;
    const here = `${location.pathname}${location.search}`;
    if (samePlace(here, place)) return;
    if (isHandPlace(here) && !run.released) return;
    lastNavRef.current = place;
    navigate(place);
  }, [place, run?.follow, location.pathname]);

  /* And stopping following the moment the user goes somewhere themselves. */
  const knownPlaceRef = useRef(null);
  useEffect(() => {
    /* Not before the run knows where it should be. */
    if (!run?.follow || !place) return;

    /* Standing on a page the run will not leave by itself is not wandering. */
    if (isHandPlace(`${location.pathname}${location.search}`)) return;

    /* And not in the commit where the run itself moved. */
    if (knownPlaceRef.current !== place) {
      knownPlaceRef.current = place;
      return;
    }

    const here = `${location.pathname}${location.search}`;
    if (lastNavRef.current && samePlace(here, lastNavRef.current)) return;
    if (place && samePlace(here, place)) return;
    setRun(current =>
      current ? { ...current, follow: false, leftAt: shownState?.step } : current
    );
  }, [location.pathname, location.search, place]);

  const start = useCallback(
    ({ workflowId, periodId, reportType, period: seed }) => {
      const next = { workflowId, periodId, reportType, follow: true, startedAt: Date.now() };
      setQuestion(null);
      setOutcome(null);
      setFired(null);
      setIsStalled(false);
      setWaitingNote(null);
      retriedRef.current = false;
      parseSummaryRef.current = null;
      attemptsRef.current = {};
      setTriedAt({});
      setRanAt({});
      setMovedAt({});
      // A new run waits on every stage again: the last run's giving up says nothing about this one.
      setAbandoned(EMPTY_ABANDONED);
      marksRef.current = {};
      workingOnRef.current = null;
      setSettled(null);
      setRun(next);

      /* Into the steps straight away, from what the caller already has. */
      const seedState = seed ? runStatus(seed, reportType) : null;
      const seedIsSure = seedState?.status === 'blocked';
      const seedPlace = seedIsSure ? placeFor(seedState, next) : null;
      if (seedPlace) {
        lastNavRef.current = seedPlace;
        navigate(seedPlace);
      }
    },
    [navigate]
  );

  const stop = useCallback(() => {
    setQuestion(null);
    setIsStalled(false);
    const busy = stateRef.current?.status === 'busy' || stateRef.current?.status === 'asked';
    if (busy) {
      setRun(current => (current ? { ...current, stopAfter: true } : current));
      return;
    }
    setRun(null);
    setFired(null);
    setWaitingNote(null);
    inFlightRef.current = null;
  }, []);

  const value = useMemo(
    () => ({
      run,
      /* The live reading, not the record as it arrived: everything downstream. */
      period: run ? readPeriod : null,
      state: shownState,
      narration: shownState && run ? runNarration(shownState, readPeriod, run.reportType) : null,
      question,
      outcome,
      isStalled,
      tiles,
      tilesCaption: tilesCaption(tiles),
      /* Only while it is still about the step being waited for. */
      waitingNote: waitingNote && waitingNote.step === fired?.step ? waitingNote.text : null,
      /* How long the stage on screen has been claiming to work without a count changing, or null. */
      stuckMinutes,
      isFollowing: !!run?.follow,
      isDriver,
      place,
      isStarting:
        copyAIFs.isLoading ||
        copyJurisdictions.isLoading ||
        automateExtraction.isLoading ||
        automateTransformation.isLoading ||
        generateReport.isLoading ||
        convertReport.isLoading,
      go: () =>
        setRun(current => (current ? { ...current, released: true, follow: true } : current)),
      /* Stop waiting on the stage the run is stalled on and let it judge the period as it stands. */
      moveOn: () => {
        const step = shownState?.step;
        if (!step) return;
        setAbandoned(prev => (prev.includes(step) ? prev : [...prev, step]));
      },
      isRunningPeriod: id => !!run && run.periodId === id,
      hold,
      /* Whether the run is doing work on this exact page right now. */
      ownsStage: (periodId, stageKey) =>
        !!run &&
        run.periodId === periodId &&
        !!shownState?.step &&
        STAGE_FOR_STEP[shownState.step] === stageKey &&
        /* From the render it decides on the step: a page's effect runs before this provider's fires it. */
        (shownState.status === 'step' ||
          shownState.status === 'asking' ||
          shownState.status === 'busy' ||
          shownState.status === 'asked' ||
          shownState.status === 'settled'),
      start,
      stop,
      /* Answering the question is the only way past it: yes takes the step, no ends the run. */
      confirm: () => {
        const step = question?.step;
        // fire() refuses while a question stands, and the ref only catches up on the next render.
        questionRef.current = null;
        setQuestion(null);
        if (step) fire(step);
      },
      /* The third answer: do the short sheets again rather than build on what got through. */
      retrySheets,
      troubled,
      canRetrySheets: !!question?.retry && !isLoadingRows && retrySheets.length > 0,
      isRetrying: reExtract.isLoading || (!!question?.retry && isLoadingRows),
      reExtract: () => {
        if (!run || !retrySheets.length) return;
        setQuestion(null);
        setFired({
          step: 'extraction',
          at: Date.now(),
          strict: true,
          mark: countsMark(readPeriod, 'extraction')
        });
        setIsStalled(false);
        reExtract.mutate(
          { period_id: run.periodId, sheet_mapping_ids: retrySheets },
          {
            onSuccess: () => {
              /* Spent only once the server has taken it. */
              retriedRef.current = true;
              invalidatePeriod(queryClient, run.periodId);
              queryClient.invalidateQueries(['GET_EXTRACTION_STATS', run.periodId]);
              invalidateWorkflow(queryClient, run.workflowId);
            },
            onError: failed('extraction')
          }
        );
      },
      /* Clears everything that can refuse the request, not just the record that one was made. */
      refire: () => {
        inFlightRef.current = null;
        setSettled(null);
        setFired(null);
        setIsStalled(false);
      },
      retry: () => {
        if (!outcome) return;
        const { workflowId, periodId, reportType } = outcome;
        setOutcome(null);
        start({ workflowId, periodId, reportType, period });
      },
      dismiss: () => setOutcome(null),
      resumeFollowing: () => {
        setRun(current => (current ? { ...current, follow: true } : current));
        if (place) {
          lastNavRef.current = place;
          navigate(place);
        }
      }
    }),
    [
      run,
      period,
      shownState,
      question,
      outcome,
      isStalled,
      isDriver,
      waitingNote,
      stuckMinutes,
      fired,
      place,
      tiles,
      retrySheets,
      troubled,
      isLoadingRows,
      reExtract.isLoading,
      fire,
      hold,
      start,
      stop,
      navigate,
      copyAIFs.isLoading,
      copyJurisdictions.isLoading,
      automateExtraction.isLoading,
      automateTransformation.isLoading,
      generateReport.isLoading,
      convertReport.isLoading
    ]
  );

  return <PeriodRunContext.Provider value={value}>{children}</PeriodRunContext.Provider>;
};
